import { useState, useRef, useEffect } from 'react';
import { Bell, Settings, LogOut, Users, Code2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './ui/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/cowork/hooks/useNotifications';
import type { Notification } from '@/cowork/hooks/useNotifications';

interface TopNavBarProps {
  currentPage?: string;
  onPageChange?: (page: string) => void;
  sidebarCollapsed?: boolean;
  userName?: string;
  brandName?: string;
}

function getDisplayName(user: { name?: string; first_name?: string; last_name?: string } | undefined): string {
  if (!user) return '';
  if (user.name) return user.name;
  const first = user.first_name ?? '';
  const last = user.last_name ?? '';
  return [first, last].filter(Boolean).join(' ') || 'User';
}

function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase() || 'U';
}

function getInitialsColor(initials: string) {
  const colors = [
    { bg: '#EDE9FE', text: '#7C3AED' },
    { bg: '#DBEAFE', text: '#2563EB' },
    { bg: '#D1FAE5', text: '#059669' },
    { bg: '#FEF3C7', text: '#D97706' },
    { bg: '#FCE7F3', text: '#DB2777' },
    { bg: '#E0E7FF', text: '#4F46E5' },
  ];
  const index = initials.charCodeAt(0) % colors.length;
  return colors[index];
}

export function TopNavBar({ currentPage: _currentPage = '', onPageChange }: TopNavBarProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const displayName = getDisplayName(user);
  const brandName = user?.team?.name ?? user?.client?.company_name ?? '';
  const userInitials = getInitials(displayName || 'User');
  const navRef = useRef<HTMLDivElement>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const notifBtnRef = useRef<HTMLButtonElement>(null);
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileBtnRef = useRef<HTMLButtonElement>(null);
  const initialsColor = getInitialsColor(userInitials);

  useEffect(() => {
    if (!showNotifs) return;
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node) &&
          notifBtnRef.current && !notifBtnRef.current.contains(e.target as Node)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNotifs]);

  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node) &&
        profileBtnRef.current && !profileBtnRef.current.contains(e.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  return (
    <div
      ref={navRef}
      className="h-14 bg-white flex items-center pr-5 shrink-0 z-50 relative"
    >
      {/* Left: Logo – generous padding, natural sizing */}
      <div className="flex items-center shrink-0 pl-5 pr-4">
        <img src="/omni-logo-dark.png" alt="Omni Ad" className="h-6 w-auto" />
      </div>

      {/* Center: spacer */}
      <div className="flex-1" />

      {/* Right: Notifications | Profile | Settings */}
      <div className="flex items-center gap-1.5 shrink-0">
        {/* Notifications */}
        <div className="relative">
          <button
            ref={notifBtnRef}
            onClick={() => setShowNotifs(!showNotifs)}
            className={cn(
              "relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors",
              showNotifs ? 'text-[#ea580c] bg-orange-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            )}
            title="Notifications"
          >
            <Bell className="w-[18px] h-[18px]" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#ea580c] px-1 text-[9px] font-bold text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifs && (
            <div
              ref={notifRef}
              className="absolute right-0 top-full mt-2 w-80 max-h-[420px] overflow-y-auto rounded-xl bg-white border border-gray-100 z-50"
              style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <span className="text-[12px] font-semibold text-gray-800">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={() => markAllRead()} className="text-[11px] text-[#be43ff] hover:underline font-medium">
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12px] text-gray-400">No notifications yet</div>
              ) : (
                notifications.slice(0, 30).map((n: Notification) => {
                  const agentLabels: Record<string, { name: string; emoji: string }> = {
                    'account-manager': { name: 'Lykke', emoji: '🤝' },
                    'leo-perf': { name: 'Leo', emoji: '📊' },
                    'maya-seo': { name: 'Maya', emoji: '🔍' },
                    'kai-analytics': { name: 'Kai', emoji: '📈' },
                    'nova-strategy': { name: 'Nova', emoji: '🧠' },
                    'rex-influencer': { name: 'Rex', emoji: '👥' },
                    'aria-content': { name: 'Aria', emoji: '✍️' },
                    'jones-artdir': { name: 'Jones', emoji: '🎨' },
                  };
                  const agent = agentLabels[n.agent_id] || { name: n.agentName || n.agent_id, emoji: n.agentEmoji || '🤖' };
                  const diff = Date.now() - new Date(n.created_at).getTime();
                  const mins = Math.floor(diff / 60000);
                  const timeAgo = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;

                  return (
                    <div
                      key={n.id}
                      onClick={() => { if (!n.read) markRead(n.id); }}
                      className={cn(
                        "px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50",
                        n.read ? 'bg-white' : 'bg-orange-50/40'
                      )}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className="text-sm shrink-0 mt-0.5">{agent.emoji}</span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-semibold text-gray-700">{agent.name}</span>
                            <span className="text-[10px] text-gray-400">{timeAgo}</span>
                            {n.urgency === 'important' && <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-600 font-medium">!</span>}
                          </div>
                          <p className="text-[12px] text-gray-600 mt-0.5 leading-relaxed line-clamp-2">{n.message}</p>
                        </div>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#ea580c] mt-2 shrink-0" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 mx-1.5" />

        {/* Brand name + Profile */}
        <div className="relative flex items-center gap-2.5">
          <span className="text-[13px] text-[#667085] hidden sm:block" style={{ fontWeight: 500 }}>{brandName}</span>
          <button
            ref={profileBtnRef}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="w-9 h-9 flex items-center justify-center rounded-full transition-all hover:ring-2 hover:ring-gray-200"
            title={displayName || 'User'}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center select-none"
              style={{ backgroundColor: initialsColor.bg, color: initialsColor.text, fontSize: 12 }}
            >
              <span style={{ fontWeight: 600, letterSpacing: '0.02em' }}>{userInitials}</span>
            </div>
          </button>

          {/* Profile dropdown */}
          {showProfileMenu && (
            <div
              ref={profileMenuRef}
              className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl border border-gray-100 py-1 z-50"
              style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm text-gray-900">{displayName || 'User'}</p>
                {brandName ? <p className="text-xs text-gray-500">{brandName}</p> : null}
              </div>
              <div className="py-1">
                <button
                  onClick={() => { setShowProfileMenu(false); onPageChange?.('settings'); }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-4 h-4 text-gray-400" />
                  {t('nav.settings')}
                </button>
                <button
                  onClick={() => { setShowProfileMenu(false); onPageChange?.('team'); }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Users className="w-4 h-4 text-gray-400" />
                  {t('nav.team')}
                </button>
                <button
                  onClick={() => { setShowProfileMenu(false); onPageChange?.('dev-mode'); }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Code2 className="w-4 h-4 text-gray-400" />
                  {t('nav.devMode')}
                </button>
              </div>
              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <LogOut className="w-4 h-4 text-gray-400" />
                  {t('nav.logOut')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
